import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Search, Filter, Check, X, Zap } from 'lucide-react';
import { authService } from '../../services/auth/authService';
import { DashboardHeader } from '../../components/layout/DashboardHeader/DashboardHeader';
import { DashboardSidebar } from '../../components/layout/DashboardSidebar/DashboardSidebar';
import { createSidebarItems } from '../../components/layout/DashboardSidebar/sidebarItems';
import {
  aiRequestsService,
  ConsultationRequest,
  AIRequestInsights,
} from '../../services/ai-requests/aiRequestsService';
import { formatDate, formatTime } from '../../utils/dateUtils';
import { getAIRequestStatusBadgeClass, getAIRequestStatusLabel } from '../../utils/statusUtils';
import './AIRequestsPage.css';

const PAGE_SIZE = 10;
const STORAGE_KEY_SEARCH = 'aiRequestsPage_search';
const STORAGE_KEY_PAGE = 'aiRequestsPage_page';

const loadStoredSearch = (): string => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_SEARCH);
    return typeof raw === 'string' ? raw : '';
  } catch {
    return '';
  }
};

const loadStoredPage = (): number => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_PAGE);
    if (raw == null) return 1;
    const n = parseInt(raw, 10);
    return Number.isFinite(n) && n >= 1 ? n : 1;
  } catch {
    return 1;
  }
};

const AIRequestsPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [requests, setRequests] = useState<ConsultationRequest[]>([]);
  const [filteredRequests, setFilteredRequests] = useState<ConsultationRequest[]>([]);
  const [insights, setInsights] = useState<AIRequestInsights>({
    total: 0,
    pending: 0,
    completed: 0,
    declined: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchText, setSearchText] = useState(loadStoredSearch);
  const [processingId, setProcessingId] = useState<number | null>(null);
  const [page, setPage] = useState(loadStoredPage);

  useEffect(() => {
    loadRequests();
  }, []);

  useEffect(() => {
    if (!searchText.trim()) {
      setFilteredRequests(requests);
    } else {
      const searchLower = searchText.toLowerCase();
      setFilteredRequests(
        requests.filter(
          (req) =>
            req.patient_name?.toLowerCase().includes(searchLower) ||
            String(req.id).includes(searchText) ||
            req.user_question?.toLowerCase().includes(searchLower) ||
            req.ai_summary?.toLowerCase().includes(searchLower)
        )
      );
    }
    setPage((p) => (searchText.trim() ? 1 : p));
  }, [searchText, requests]);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY_SEARCH, searchText);
    } catch {
      // ignore
    }
  }, [searchText]);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY_PAGE, String(page));
    } catch {
      // ignore
    }
  }, [page]);

  const loadRequests = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await aiRequestsService.getConsultationRequests();
      setRequests(data);
      setFilteredRequests(data);
      setInsights(aiRequestsService.calculateInsights(data));
    } catch (err: any) {
      console.error('Failed to load AI requests:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to load AI requests');
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async (requestId: number) => {
    try {
      setProcessingId(requestId);
      await aiRequestsService.updateRequestStatus(requestId, 'pending');
      await loadRequests();
    } catch (err: any) {
      console.error('Failed to accept request:', err);
      alert(err.response?.data?.detail || 'Failed to accept request');
    } finally {
      setProcessingId(null);
    }
  };

  const handleDecline = async (requestId: number) => {
    try {
      setProcessingId(requestId);
      await aiRequestsService.updateRequestStatus(requestId, 'rejected');
      await loadRequests();
    } catch (err: any) {
      console.error('Failed to decline request:', err);
      alert(err.response?.data?.detail || 'Failed to decline request');
    } finally {
      setProcessingId(null);
    }
  };

  const handleLogout = () => {
    authService.clearTokens();
    navigate('/login');
  };

  const handleGoToProfile = () => {
    navigate('/profile');
  };

  const sidebarItems = createSidebarItems(navigate, location.pathname);

  const getPatientName = (request: ConsultationRequest): string => {
    if (request.patient_name) return request.patient_name;
    if (request.context) {
      try {
        const context = JSON.parse(request.context);
        return context.patient_name || 'Patient';
      } catch {
        return 'Patient';
      }
    }
    return 'Patient';
  };

  const getRequestPreview = (request: ConsultationRequest): string => {
    const text = request.user_question || request.ai_summary || 'No preview available';
    return text.length > 60 ? text.substring(0, 60) + '...' : text;
  };

  const totalPages = Math.max(1, Math.ceil(filteredRequests.length / PAGE_SIZE));
  const start = (page - 1) * PAGE_SIZE;
  const paginatedRequests = filteredRequests.slice(start, start + PAGE_SIZE);

  return (
    <div className="dashboard">
      <DashboardHeader onProfileClick={handleGoToProfile} onLogout={handleLogout} />

      <div className="dashboard-layout">
        <DashboardSidebar items={sidebarItems} />

        <div className="dashboard-container">
          <main className="dashboard-main-content ai-requests-page">
            {loading && (
              <div className="ai-requests-loading">
                Loading AI requests...
              </div>
            )}

            {error && (
              <div className="ai-requests-error">
                Error: {error}
              </div>
            )}

            {!loading && !error && (
              <div className="ai-requests-card">
                {/* Section 1: Header + description left, status cards right */}
                <header className="ai-requests-header">
                  <div className="ai-requests-header-text">
                    <h1 className="ai-requests-title">AI Requests Dashboard</h1>
                    <p className="ai-requests-description">
                      Manage and respond to clinical AI-generated patient summaries and medical record inquiries.
                    </p>
                  </div>
                  <div className="ai-requests-status-cards">
                    <div className="ai-requests-status-card ai-requests-status-total">
                      <span className="ai-requests-status-label">TOTAL</span>
                      <span className="ai-requests-status-value">{insights.total}</span>
                    </div>
                    <div className="ai-requests-status-card ai-requests-status-pending">
                      <span className="ai-requests-status-label">PENDING</span>
                      <span className="ai-requests-status-value">{insights.pending}</span>
                    </div>
                    <div className="ai-requests-status-card ai-requests-status-done">
                      <span className="ai-requests-status-label">DONE</span>
                      <span className="ai-requests-status-value">{insights.completed}</span>
                    </div>
                    <div className="ai-requests-status-card ai-requests-status-declined">
                      <span className="ai-requests-status-label">DECLINED</span>
                      <span className="ai-requests-status-value">{insights.declined}</span>
                    </div>
                  </div>
                </header>

                {/* Section 2: Search + Filters (no Export List) */}
                <div className="ai-requests-toolbar">
                  <div className="ai-requests-search-wrap">
                    <Search size={18} strokeWidth={2} className="ai-requests-search-icon" aria-hidden />
                    <input
                      type="text"
                      className="ai-requests-search-input"
                      placeholder="Search by patient name or request ID..."
                      value={searchText}
                      onChange={(e) => setSearchText(e.target.value)}
                      aria-label="Search by patient name or request ID"
                    />
                  </div>
                  <button type="button" className="ai-requests-filters-btn" aria-label="Filters">
                    <Filter size={18} strokeWidth={2} />
                    Filters
                  </button>
                </div>

                {/* Section 3: Table */}
                <section className="ai-requests-section">
                  {filteredRequests.length === 0 ? (
                    <div className="ai-requests-empty-state">
                      {searchText ? 'No requests match your search.' : 'No AI requests found.'}
                    </div>
                  ) : (
                    <>
                      <div className="ai-requests-table-wrap">
                        <table className="ai-requests-table">
                          <thead>
                            <tr>
                              <th>PATIENT INFORMATION</th>
                              <th>REQUEST CONTENT</th>
                              <th>TIME STAMP</th>
                              <th>STATUS</th>
                              <th>MANAGEMENT</th>
                            </tr>
                          </thead>
                          <tbody>
                            {paginatedRequests.map((request) => {
                              const patientName = getPatientName(request);
                              const initials = patientName
                                .split(' ')
                                .map((n) => n[0])
                                .join('')
                                .toUpperCase()
                                .substring(0, 2);
                              const statusLower = (request.status || '').toLowerCase();
                              const isRequested = statusLower === 'requested';
                              const isPending = statusLower === 'pending';
                              const isCompleted = statusLower === 'completed';
                              const isRejected = statusLower === 'rejected' || statusLower === 'declined';
                              const isProcessing = processingId === request.id;
                              const statusClass = getAIRequestStatusBadgeClass(request.status, false);

                              return (
                                <tr key={request.id}>
                                  <td>
                                    <div className="ai-requests-patient-cell">
                                      <div
                                        className={`ai-requests-patient-avatar ${isRequested ? 'active-request' : ''}`}
                                      >
                                        {initials}
                                      </div>
                                      <div className="ai-requests-patient-info">
                                        <span className="ai-requests-patient-name">{patientName}</span>
                                        {isRequested ? (
                                          <span className="ai-requests-active-tag">ACTIVE REQUEST</span>
                                        ) : (
                                          <span className="ai-requests-id">ID: #{request.id}</span>
                                        )}
                                      </div>
                                    </div>
                                  </td>
                                  <td>
                                    <div className="ai-requests-content-cell">
                                      <span className="ai-requests-content-preview">{getRequestPreview(request)}</span>
                                      {isRequested && (
                                        <span className="ai-requests-priority-tag">
                                          <Zap size={12} strokeWidth={2} />
                                          HIGH PRIORITY
                                        </span>
                                      )}
                                    </div>
                                  </td>
                                  <td>
                                    <div className="ai-requests-timestamp-cell">
                                      <span className="ai-requests-date">{formatDate(request.created_at)}</span>
                                      <span className="ai-requests-time">{formatTime(request.created_at)}</span>
                                    </div>
                                  </td>
                                  <td className="ai-requests-status-cell">
                                    <span className={`ai-requests-status-dot ${statusClass}`} aria-hidden />
                                    <span className="ai-requests-status-text">
                                      {getAIRequestStatusLabel(request.status)}
                                    </span>
                                  </td>
                                  <td>
                                    <div className="ai-requests-management-cell">
                                      {isRequested && (
                                        <>
                                          <button
                                            type="button"
                                            className="ai-requests-btn-accept"
                                            onClick={() => handleAccept(request.id)}
                                            disabled={isProcessing}
                                          >
                                            <Check size={16} strokeWidth={2} />
                                            Accept
                                          </button>
                                          <button
                                            type="button"
                                            className="ai-requests-btn-decline"
                                            onClick={() => handleDecline(request.id)}
                                            disabled={isProcessing}
                                            aria-label="Decline"
                                          >
                                            <X size={16} strokeWidth={2} />
                                          </button>
                                        </>
                                      )}
                                      {(isPending || isCompleted) && (
                                        <button
                                          type="button"
                                          className="ai-requests-btn-details"
                                          onClick={() => navigate(`/ai-requests/${request.id}`)}
                                        >
                                          Details
                                        </button>
                                      )}
                                      {isRejected && <span className="ai-requests-no-action">—</span>}
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>

                      {/* Footer: count + pagination */}
                      <footer className="ai-requests-footer">
                        <span className="ai-requests-footer-count">
                          Showing {paginatedRequests.length} of {filteredRequests.length} active clinical AI requests
                        </span>
                        <div className="ai-requests-pagination">
                          <button
                            type="button"
                            className="ai-requests-page-btn"
                            onClick={() => setPage((p) => Math.max(1, p - 1))}
                            disabled={page <= 1}
                            aria-label="Previous page"
                          >
                            ←
                          </button>
                          <span className="ai-requests-page-num">{page}</span>
                          <button
                            type="button"
                            className="ai-requests-page-btn"
                            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                            disabled={page >= totalPages}
                            aria-label="Next page"
                          >
                            →
                          </button>
                        </div>
                      </footer>
                    </>
                  )}
                </section>
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};

export default AIRequestsPage;
